import { z } from 'zod';
import { binName } from './bins';
import { joinList, type Locale } from './i18n';
import { addDays, daysBetween, toMinutes, weekdayOf } from './schedule';

/**
 * Erinnerungen (Release 1.13): what reaches the phones, when, and to whom – as pure logic, so
 * the server's 5-minute schedule and the tests agree. Family defaults live on the household,
 * a single event or calendar can differ ("Erinnern").
 */

const Clock = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'HH:MM');

/** How early to remind of one event: minutes before, the evening before, or not at all. */
export const Remind = z.union([
  z.literal('none'),
  z.literal('eve'),
  z
    .number()
    .int()
    .min(5)
    .max(24 * 60),
]);
export type Remind = z.infer<typeof Remind>;

export const ReminderSettings = z.object({
  /** Minutes before events with a time; 0 = off. */
  eventMinutes: z
    .number()
    .int()
    .min(0)
    .max(24 * 60),
  /** Hour on the evening before all-day events (and the bins); null = off. */
  eveHour: z.number().int().min(12).max(22).nullable(),
  /** "Trinkflasche einpacken" two hours before events with things to take. */
  bring: z.boolean(),
  /** Tasks waiting for approval and reward wishes, right away. */
  approvals: z.boolean(),
  /** A week before birthdays in the family (and on the day). */
  birthdays: z.boolean(),
  /** No messages in between (overnight windows allowed). */
  quietFrom: Clock,
  quietTo: Clock,
});
export type ReminderSettings = z.infer<typeof ReminderSettings>;

export const REMINDER_DEFAULTS: ReminderSettings = {
  eventMinutes: 30,
  eveHour: 18,
  bring: true,
  approvals: true,
  birthdays: true,
  quietFrom: '22:00',
  quietTo: '07:00',
};

/** "MM-DD" – a birthday without the year (as little data as possible). */
export const Birthday = z.string().regex(/^(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/, 'MM-DD');

/** Is this time inside the quiet window? Works across midnight (22:00–07:00). */
export function inQuiet(time: string, from: string, to: string): boolean {
  const t = toMinutes(time);
  const a = toMinutes(from);
  const b = toMinutes(to);
  if (a === b) return false;
  return a < b ? t >= a && t < b : t >= a || t < b;
}

// ---------------------------------------------------------------------------------------------
// Due reminders in one schedule window
// ---------------------------------------------------------------------------------------------

export interface ReminderEvent {
  id: string;
  title: string;
  /** the day of this occurrence (series are expanded by the caller) */
  date: string;
  start?: string;
  /** member ids or 'all' */
  assignees: string[] | 'all';
  /** "Ben, Lotta" */
  who: string;
  bring: string[];
  transport?: string;
  remind?: Remind;
  waste?: boolean;
}

export interface ReminderMember {
  id: string;
  name: string;
  role: 'parent' | 'teen' | 'kid';
  birthday?: string;
}

export interface DueReminder {
  /** stable – a reminder is sent once (the server keeps the keys of what it sent) */
  key: string;
  title: string;
  body: string;
  /** where tapping leads */
  url: string;
  /** member ids who get it (on their own phones) */
  to: string[];
}

/** Push texts in the Familiensprache (German master, English 1.19). */
export const REMINDER_TEXT = {
  de: {
    inMin: (m: string) => `in ${m} Min.`,
    inHours: (h: string) => `in ${h} Std.`,
    inHoursMin: (h: string, m: string) => `in ${h} Std. ${m} Min.`,
    pack: (items: string) => `${items} einpacken`,
    at: (time: string) => `${time} Uhr`,
    putOut: (bin: string) => `Heute Abend rausstellen: ${bin}`,
    tomorrow: (what: string) => `Morgen: ${what}`,
    lay: (items: string) => `${items} bereitlegen`,
    allDay: 'Ganztägig',
    birthday: (name: string) => `Heute hat ${name} Geburtstag!`,
    birthdayBody: 'Alles Gute – feiert schön.',
    birthdaySoon: (name: string) => `${name} hat in einer Woche Geburtstag`,
    birthdaySoonBody: 'Ein Blick auf den Wunschzettel?',
    someone: 'Jemand',
    taskDone: (who: string, task: string) => `${who} hat „${task}“ erledigt`,
    starsWaiting: (n: string) => `${n} Sterne warten auf deine Freigabe`,
    wantsReward: (who: string, reward: string) => `${who} möchte „${reward}“ einlösen`,
    aReward: 'eine Belohnung',
    rewardBody: (n: string) => `${n} Sterne – magst du zustimmen?`,
  },
  en: {
    inMin: (m: string) => `in ${m} min`,
    inHours: (h: string) => `in ${h} hr`,
    inHoursMin: (h: string, m: string) => `in ${h} hr ${m} min`,
    pack: (items: string) => `Pack ${items}`,
    at: (time: string) => `at ${time}`,
    putOut: (bin: string) => `Put out tonight: ${bin}`,
    tomorrow: (what: string) => `Tomorrow: ${what}`,
    lay: (items: string) => `Get ${items} ready`,
    allDay: 'All day',
    birthday: (name: string) => `It’s ${name}’s birthday today!`,
    birthdayBody: 'Happy birthday – have a lovely day.',
    birthdaySoon: (name: string) => `${name}’s birthday is in a week`,
    birthdaySoonBody: 'Time for a look at the wish list?',
    someone: 'Someone',
    taskDone: (who: string, task: string) => `${who} has done “${task}”`,
    starsWaiting: (n: string) => `${n} stars waiting for your approval`,
    wantsReward: (who: string, reward: string) => `${who} would like to redeem “${reward}”`,
    aReward: 'a reward',
    rewardBody: (n: string) => `${n} stars – would you like to approve?`,
  },
} as const;

const label = (e: ReminderEvent) => (e.who ? `${e.title} (${e.who})` : e.title);
const inWords = (min: number, locale: Locale) => {
  const t = REMINDER_TEXT[locale];
  if (min < 60) return t.inMin(String(min));
  const h = String(Math.floor(min / 60));
  return min % 60 ? t.inHoursMin(h, String(min % 60)) : t.inHours(h);
};

interface ReminderDay {
  today: string;
  settings: ReminderSettings;
  /** events of today and tomorrow */
  events: ReminderEvent[];
  members: ReminderMember[];
  /** the Familiensprache; missing = German */
  locale?: Locale;
}

/**
 * Everything to send whose moment falls into (from, to] – minutes since today 00:00 in the
 * household's time. Events are those of today and tomorrow. Parents always get event
 * reminders; teens also for their own events (Mike, 04.10.).
 */
export function dueReminders(input: ReminderDay & { from: number; to: number }): DueReminder[] {
  return allReminders(input)
    .filter((r) => r.minute > input.from && r.minute <= input.to)
    .map(({ minute: _, ...r }) => r);
}

/**
 * The next moment the server has to look at this family (1.17, "Hintergrund-Jobs sparsam"):
 * minutes after today 00:00 of the first reminder after `now` outside the quiet hours – today,
 * else tomorrow (+1440). Undefined when nothing is due today or tomorrow.
 * `events` holds today, tomorrow and the day after (tomorrow's evening reminders need it).
 */
export function nextReminderMinute(input: ReminderDay & { now: number }): number | undefined {
  const { today, now, settings: s } = input;
  const audible = (minute: number) => !inQuiet(clockOf(minute), s.quietFrom, s.quietTo);
  const tomorrow = addDays(today, 1);
  const later = allReminders({ ...input, today: tomorrow }).map((r) => r.minute + 24 * 60);
  const minutes = [...allReminders(input).map((r) => r.minute), ...later]
    .filter((m) => m > now && audible(((m % 1440) + 1440) % 1440))
    .sort((a, b) => a - b);
  return minutes[0];
}

/** 75 → "01:15" (minutes of a day, wrapped). */
const clockOf = (minute: number) => {
  const m = ((minute % 1440) + 1440) % 1440;
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
};

/** Every reminder of one day with the minute it is due (may be < 0: the evening before). */
function allReminders(input: ReminderDay): (DueReminder & { minute: number })[] {
  const { today, settings: s, events, members, locale = 'de' } = input;
  const t = REMINDER_TEXT[locale];
  const list = (items: string[]) => joinList(items, locale);
  const tomorrow = addDays(today, 1);
  const parents = members.filter((m) => m.role === 'parent').map((m) => m.id);
  const teensOf = (e: ReminderEvent) =>
    members
      .filter((m) => m.role === 'teen' && (e.assignees === 'all' || e.assignees.includes(m.id)))
      .map((m) => m.id);
  const out: (DueReminder & { minute: number })[] = [];

  for (const e of events) {
    const remind = e.remind ?? null;
    if (remind === 'none') continue;
    const recipients = [...new Set([...parents, ...teensOf(e)])];
    const url = '/kalender';

    if (e.start && e.date === today) {
      const start = toMinutes(e.start);
      const before = typeof remind === 'number' ? remind : remind === 'eve' ? null : s.eventMinutes;
      if (before) {
        const extra = [e.transport, e.bring.length ? t.pack(list(e.bring)) : '']
          .filter(Boolean)
          .join(' · ');
        out.push({
          minute: start - before,
          key: `event:${e.id}:${e.date}`,
          title: `${label(e)} ${inWords(before, locale)}`,
          body: extra || t.at(e.start),
          url,
          to: recipients,
        });
      }
      // things to take: two hours ahead, unless the reminder itself comes earlier anyway
      if (s.bring && e.bring.length && (before ?? 0) < 120) {
        out.push({
          minute: start - 120,
          key: `bring:${e.id}:${e.date}`,
          title: t.pack(list(e.bring)),
          body: `${e.start} ${label(e)}`,
          url,
          to: recipients,
        });
      }
    }

    // the evening before: all-day events, the bins, and events set to "Vorabend"
    const eve = remind === 'eve' || (!e.start && remind === null);
    if (eve && s.eveHour !== null && e.date === tomorrow) {
      out.push({
        minute: s.eveHour * 60,
        key: `eve:${e.id}:${e.date}`,
        title: e.waste ? t.putOut(binName(e.title, locale)) : t.tomorrow(label(e)),
        body: e.start
          ? t.at(e.start) + (e.bring.length ? ` · ${t.lay(list(e.bring))}` : '')
          : e.bring.length
            ? t.lay(list(e.bring))
            : t.allDay,
        url,
        to: recipients,
      });
    }
  }

  if (s.birthdays) {
    const morning = 8 * 60;
    const week = addDays(today, 7);
    for (const m of members) {
      if (!m.birthday) continue;
      if (m.birthday === today.slice(5)) {
        out.push({
          minute: morning,
          key: `birthday:${m.id}:${today}`,
          title: t.birthday(m.name),
          body: t.birthdayBody,
          url: '/',
          to: members.map((x) => x.id),
        });
      }
      if (m.birthday === week.slice(5) && s.eveHour !== null) {
        out.push({
          minute: s.eveHour * 60,
          key: `birthday-soon:${m.id}:${week}`,
          title: t.birthdaySoon(m.name),
          body: t.birthdaySoonBody,
          url: `/freizeit/wuensche/${m.id}`,
          // a surprise stays one: not to the birthday child
          to: members.filter((x) => x.id !== m.id).map((x) => x.id),
        });
      }
    }
  }
  return out;
}

// ---------------------------------------------------------------------------------------------
// Birthdays and festive days on "Heute" and the resting wall
// ---------------------------------------------------------------------------------------------

/** Days until the next birthday (0 = today). */
export function daysToBirthday(today: string, birthday: string): number {
  const year = Number(today.slice(0, 4));
  let next = `${year}-${birthday}`;
  // 29 February in a normal year: celebrate on the 28th
  if (birthday === '02-29' && !isLeap(year)) next = `${year}-02-28`;
  if (next < today)
    next =
      birthday === '02-29' && !isLeap(year + 1) ? `${year + 1}-02-28` : `${year + 1}-${birthday}`;
  return daysBetween(today, next);
}
const isLeap = (y: number) => (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;

/** Easter Sunday (Gregorian, anonymous algorithm). */
export function easterSunday(year: number): string {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** The fourth Advent is the last Sunday before 25 December. */
function adventSundays(year: number): string[] {
  const christmas = `${year}-12-25`;
  const fourth = addDays(christmas, -(weekdayOf(christmas) % 7 || 7));
  return [21, 14, 7, 0].map((n) => addDays(fourth, -n));
}

export type FestivalKey =
  'advent' | 'nikolaus' | 'christmas' | 'newyearseve' | 'newyear' | 'easter' | 'halloween';

export interface Festival {
  key: FestivalKey;
  /** the line on "Heute" */
  text: string;
  /** big moment: the festive view pops up once per device */
  big: boolean;
  /** Advent: candles lit (1–4) */
  candles?: number;
}

/** Names (settings) and the line on "Heute" (German master, English 1.19). */
export const FESTIVAL_TEXT = {
  de: {
    names: {
      advent: 'Advent',
      nikolaus: 'Nikolaus',
      christmas: 'Weihnachten',
      newyearseve: 'Silvester',
      newyear: 'Neujahr',
      easter: 'Ostern',
      halloween: 'Halloween',
    } satisfies Record<FestivalKey, string>,
    christmas: 'Frohe Weihnachten!',
    newyearseve: 'Silvester – heute Nacht wird gefeiert',
    newyear: (year: string) => `Frohes neues Jahr ${year}!`,
    nikolaus: 'Heute ist Nikolaus – Stiefel geputzt?',
    halloween: 'Happy Halloween!',
    easter: 'Frohe Ostern!',
    adventSunday: (n: string, left: string) => `${n}. Advent – noch ${left} Tage bis Weihnachten`,
    toChristmas: (left: string) => `Noch ${left} Tage bis Weihnachten`,
  },
  en: {
    names: {
      advent: 'Advent',
      nikolaus: 'St Nicholas’ Day',
      christmas: 'Christmas',
      newyearseve: 'New Year’s Eve',
      newyear: 'New Year’s Day',
      easter: 'Easter',
      halloween: 'Halloween',
    } satisfies Record<FestivalKey, string>,
    christmas: 'Merry Christmas!',
    newyearseve: 'New Year’s Eve – tonight we celebrate',
    newyear: (year: string) => `Happy New Year ${year}!`,
    nikolaus: 'It’s St Nicholas’ Day – boots polished?',
    halloween: 'Happy Halloween!',
    easter: 'Happy Easter!',
    adventSunday: (n: string, left: string) =>
      `${n} Sunday of Advent – ${left} days until Christmas`,
    toChristmas: (left: string) => `${left} days until Christmas`,
  },
} as const;

const ORDINAL_EN = ['1st', '2nd', '3rd', '4th'];

/** Which festive day (if any) colours this day. Christmas wins over the fourth Advent. */
export function festivalOn(
  day: string,
  off: FestivalKey[] = [],
  locale: Locale = 'de',
): Festival | null {
  const t = FESTIVAL_TEXT[locale];
  const year = Number(day.slice(0, 4));
  const md = day.slice(5);
  const pick = (f: Festival | null) => (f && !off.includes(f.key) ? f : null);
  if (md === '12-24' || md === '12-25' || md === '12-26')
    return pick({ key: 'christmas', text: t.christmas, big: md === '12-24' });
  if (md === '12-31') return pick({ key: 'newyearseve', text: t.newyearseve, big: false });
  if (md === '01-01') return pick({ key: 'newyear', text: t.newyear(String(year)), big: true });
  if (md === '12-06') return pick({ key: 'nikolaus', text: t.nikolaus, big: false });
  if (md === '10-31') return pick({ key: 'halloween', text: t.halloween, big: false });
  const easter = easterSunday(year);
  if (day === easter || day === addDays(easter, 1))
    return pick({ key: 'easter', text: t.easter, big: day === easter });
  const sundays = adventSundays(year);
  if (day >= sundays[0]! && md < '12-24') {
    const lit = sundays.filter((s) => s <= day).length;
    const left = daysBetween(day, `${year}-12-24`);
    const sunday = sundays.includes(day);
    return pick({
      key: 'advent',
      text: sunday
        ? t.adventSunday(locale === 'de' ? String(lit) : ORDINAL_EN[lit - 1]!, String(left))
        : t.toChristmas(String(left)),
      big: false,
      candles: lit,
    });
  }
  return null;
}

/** Festive keys a family can switch off; Halloween is off unless chosen. */
export const FESTIVE_DEFAULT_OFF: FestivalKey[] = ['halloween'];
